import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Table } from 'antd';
import { api } from './api/http';
import { useAuth } from './auth/AuthProvider';
import AppLayout from './layout/AppLayout';

export default function CustomerSelection() {
  const navigate = useNavigate();
  const { session } = useAuth();

  const role = String(session?.role || '').toLowerCase();
  const userId = session?.userId ?? null;

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [selectedRowId, setSelectedRowId] = useState(null);

  const clearSelectionStorage = useCallback(() => {
    try {
      localStorage.removeItem('customerId');
      localStorage.removeItem('selectedCustomerId');
      localStorage.removeItem('customerName');
      localStorage.removeItem('pendingCustomerId');
      localStorage.removeItem('selectedCustomerRole');
    } catch {}
    try {
      const raw = localStorage.getItem('session');
      const base = raw ? JSON.parse(raw) : {};
      delete base.customerId;
      delete base.customerName;
      delete base.pendingCustomerId;
      localStorage.setItem('session', JSON.stringify(base));
    } catch {}
  }, []);

  const setActiveCustomerStorage = useCallback((cid, cname) => {
    try {
      localStorage.setItem('customerId', String(cid));
      localStorage.setItem('selectedCustomerId', String(cid));
      localStorage.setItem('customerName', String(cname || ''));
      localStorage.removeItem('selectedCustomerRole');
      localStorage.removeItem('pendingCustomerId');
    } catch {}

    try {
      const raw = localStorage.getItem('session');
      const base = raw ? JSON.parse(raw) : {};
      base.customerId = cid;
      base.customerName = cname;
      delete base.selectedCustomerRole;
      delete base.pendingCustomerId;
      localStorage.setItem('session', JSON.stringify(base));
    } catch {
      localStorage.setItem(
        'session',
        JSON.stringify({
          customerId: cid,
          customerName: cname,
        })
      );
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setErr('');
    try {
      let customers = [];

      if (role === 'admin') {
        const { data } = await api.get('/api/customers');
        customers = Array.isArray(data)
          ? data.map((c) => ({
              id: Number(c.id),
              name: c.name ?? c.customerName ?? `Customer #${c.id}`,
            }))
          : [];
      } else if (userId != null) {
        const { data } = await api.get(`/api/users/${userId}/customers`, { skipCustomer: true });
        const rows = Array.isArray(data) ? data : [];
        const uniq = [];
        for (const r of rows) {
          const id = Number(r.customer_id ?? r.id);
          const name = r.name ?? r.customerName ?? `Customer #${id}`;
          if (!uniq.some((x) => x.id === id)) uniq.push({ id, name });
        }
        customers = uniq;
      }

      if (customers.length === 0 && (role === 'technician' || role === 'operator')) {
        clearSelectionStorage();
        setList([]);
        setSelectedRowId(null);
        navigate('/invites', { replace: true });
        return;
      }

      if (customers.length === 1 && (role === 'technician' || role === 'operator')) {
        const cid = Number(customers[0].id);
        const cname = String(customers[0].name || `Customer #${cid}`);
        setActiveCustomerStorage(cid, cname);
        const dest = role === 'technician' ? '/technician-dashboard' : '/operator';
        navigate(dest, { replace: true });
        return;
      }

      setList(customers);

      if (customers.length > 1) {
        let pending = null;
        try {
          pending = Number(localStorage.getItem('pendingCustomerId') || 0) || null;
        } catch {}
        if (pending && customers.some((c) => Number(c.id) === pending)) {
          setSelectedRowId(pending);
        } else {
          setSelectedRowId(null);
        }
      }
    } catch {
      setErr('Failed to load customers.');
      setList([]);
      setSelectedRowId(null);
    } finally {
      setLoading(false);
    }
  }, [role, userId, navigate, clearSelectionStorage, setActiveCustomerStorage]);

  useEffect(() => {
    load();
  }, [load]);

  const homeFor = (r) => {
    if (r === 'technician') return '/technician-dashboard';
    if (r === 'operator') return '/operator';
    if (r === 'customer') return '/fleet_dashboard';
    return '/dashboard';
  };

  const pick = (c) => {
    if (!c?.id) return;
    const cid = Number(c.id);
    const cname = String(c.name || `Customer #${cid}`);
    setActiveCustomerStorage(cid, cname);
    navigate(homeFor(role), { replace: true });
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            type="primary"
            onClick={() => pick(record)}
            style={{ borderRadius: 999, padding: '0 18px', height: 36 }}
          >
            Use this account
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppLayout title="Select Account">
      <div style={{ maxWidth: 960, margin: '0 auto', padding: '24px 16px 32px' }}>
        <Card
          style={{
            borderRadius: 20,
            boxShadow: '0 8px 24px rgba(15, 35, 52, 0.12)',
            border: '1px solid rgba(15, 35, 52, 0.06)',
            background:
              'linear-gradient(180deg, rgba(245, 247, 250, 0.9), rgba(255, 255, 255, 0.95))',
          }}
          bodyStyle={{ padding: 24 }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16,
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: 'black',
                  textTransform: 'none',
                  letterSpacing: 0.02,
                  marginBottom: 4,
                }}
              >
                Select Account
              </div>
              <div style={{ fontSize: 12, color: '#808080', marginBottom: 0 }}>
                Choose which account you want to work with today.
              </div>
            </div>
          </div>

          {err && <div style={{ marginBottom: 12, color: 'red' }}>{err}</div>}

          <div className="tableWrap">
            <Table
              rowKey="id"
              dataSource={list}
              columns={columns}
              pagination={false}
              bordered
              size="middle"
              loading={loading}
              locale={{ emptyText: 'No customers.' }}
              rowClassName={(record) => (record.id === selectedRowId ? 'ant-table-row-selected' : '')}
              onRow={(record) => ({
                onClick: () => setSelectedRowId(record.id),
              })}
              scroll={{ x: 'max-content' }}
            />
          </div>
        </Card>
      </div>
    </AppLayout>
  );
}
